import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";

import { getQueryKey as getTopicsListQueryKey } from "@app/analytics/hooks/topics/useTopicReports";
import * as Events from "@app/analytics/tracking/events";
import type { Topic, TopicImpersonationView } from "@app/analytics/types";
import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import type { User } from "@app/common/types";
import { hasRetryableStatus } from "@app/common/utils";

import { getQueryKey as getTopicQueryKey } from "./useTopicReport";

const EMPTY_IMPERSONATIONS: TopicImpersonationView[] = [];

const getQueryKey = (topicId?: Topic["id"], userId?: User["id"]) => [
  "topic",
  topicId,
  userId,
  "impersonations",
];

const useTopicReportImpersonations = (
  { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
  const queryClient = useQueryClient();

  const { user } = useAuth();
  const { topicId } = useParams();

  const queryKey = getQueryKey(topicId, user?.id);

  const impersonationsQuery = useQuery({
    queryKey,
    queryFn: () => {
      if (!topicId) return;
      return apiClient.topics.listImpersonations(topicId);
    },
    enabled: enabled && Boolean(topicId) && Boolean(user?.id),
    retry: (failureCount, error) =>
      failureCount < 3 && hasRetryableStatus(error),
  });

  const createImpersonationsMutation = useMutation({
    mutationFn: apiClient.topics.createImpersonations,
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const deleteImpersonationMutation = useMutation({
    mutationFn: apiClient.topics.deleteImpersonation,
    onSuccess: () => {
      Events.deleteReport({ reportType: "readout" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: getTopicQueryKey({ topicId, userId: user?.id }),
      });
      queryClient.invalidateQueries({
        queryKey: getTopicsListQueryKey(user?.id),
      });
    },
  });

  const impersonations = impersonationsQuery.data ?? EMPTY_IMPERSONATIONS;

  return {
    isLoading: impersonationsQuery.isLoading,
    isError: impersonationsQuery.isError,

    createImpersonations: createImpersonationsMutation,
    deleteImpersonation: deleteImpersonationMutation,
    impersonations,
  };
};

export default useTopicReportImpersonations;
